// AI Ghostwriter Pro - Pricing Page
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { CheckCircle, Star, Zap, Crown } from 'lucide-react';

const PACKAGES = [
  {
    id: 'starter',
    name: 'Starter',
    price: 497,
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    description: 'Perfect for your first ebook or short book',
    features: [
      'Up to 20,000 words',
      '10 chapters',
      '3-5 day delivery',
      '1 revision round',
      'Amazon-optimized description',
      'Basic keyword research',
      'Plagiarism-free guarantee',
      'Full copyright ownership'
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 997,
    icon: Star,
    color: 'from-purple-500 to-pink-500',
    popular: true,
    description: 'Our most popular package for serious authors',
    features: [
      'Up to 50,000 words',
      '25 chapters',
      '7-10 day delivery',
      '3 revision rounds',
      'Amazon-optimized description',
      'Advanced keyword research',
      'Professional book cover design',
      'Plagiarism-free guarantee',
      'Full copyright ownership',
      'Author bio creation'
    ]
  },
  {
    id: 'complete',
    name: 'Complete',
    price: 1497,
    icon: Crown,
    color: 'from-amber-500 to-orange-500',
    description: 'The full publishing package with everything included',
    features: [
      'Up to 70,000 words',
      'Unlimited chapters',
      '10-14 day delivery',
      'Unlimited revisions',
      'Amazon-optimized description',
      'Kindle Research Pro access',
      'Premium book cover design',
      'Niche market analysis',
      'Plagiarism-free guarantee',
      'Full copyright ownership',
      'Author bio + media kit',
      'Launch strategy guide'
    ]
  }
];

export function GhostwriterPricing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSelect = (pkgId) => {
    if (user) {
      navigate(`/ghostwriter/dashboard?package=${pkgId}`);
    } else {
      navigate(`/register?redirect=/ghostwriter/dashboard&package=${pkgId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SeoHead
        title="Pricing | AI Ghostwriter Pro - Book Writing Packages from $497"
        description="Choose your AI ghostwriting package. Starter $497 (20K words), Professional $997 (50K words), Complete $1,497 (70K words). All packages include full copyright ownership."
        canonical="https://aighostwriterpro.com/pricing"
        og={{ type: 'website', url: 'https://aighostwriterpro.com/pricing', title: 'AI Ghostwriter Pro Pricing - Starting at $497', description: 'Professional AI book writing packages from $497. Complete manuscripts in 7-14 days.', siteName: 'AI Ghostwriter Pro' }}
        twitter={{ card: 'summary_large_image', title: 'AI Ghostwriter Pro Pricing', description: 'Book writing packages from $497.' }}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="pricing-heading">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Choose the package that fits your book. All packages include full copyright ownership, plagiarism-free guarantee, and Amazon optimization.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {PACKAGES.map((pkg) => {
            const Icon = pkg.icon;
            return (
              <div key={pkg.id} className={`relative rounded-2xl border ${pkg.popular ? 'border-purple-400 ring-2 ring-purple-400' : 'border-slate-700'} bg-slate-800/50 backdrop-blur-sm overflow-hidden`} data-testid={`pricing-card-${pkg.id}`}>
                {pkg.popular && (
                  <div className="bg-purple-500 text-white text-xs font-bold text-center py-1.5 uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${pkg.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">{pkg.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{pkg.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">${pkg.price.toLocaleString()}</span>
                    <span className="text-gray-400 ml-2">one-time</span>
                  </div>
                  <Button
                    onClick={() => handleSelect(pkg.id)}
                    className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                    data-testid={`pricing-select-${pkg.id}`}
                  >
                    Get Started
                  </Button>
                  <ul className="mt-6 space-y-3">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-3">100% Satisfaction Guarantee</h3>
            <p className="text-gray-400">
              Not happy with your outline? We'll revise it until you approve. Not satisfied after the first 3 chapters? We'll refund your full payment. No questions asked.
            </p>
          </div>
        </div>

        {/* Promo Code Note */}
        <p className="text-center text-gray-500 text-sm mt-8">
          Have a promo code? You can apply it at checkout.
        </p>
      </div>
    </div>
  );
}

export default GhostwriterPricing;
